import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { GetVehicleResponse } from '@/api/generated/schemas';
import { FuelType } from '@/api/generated/schemas';
import { dateToIso } from '@/shared/ui';

/** UI ref položky číselníku (kategorie, skupina, značka, typ dokumentu). */
const codebookRefSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().nullable(),
});

/** UI ref vlastníka (organizace přes stabilní `Party.ID`). */
const organizationRefSchema = z.object({
  partyId: z.number(),
  name: z.string(),
});

/** UI ref preferovaného řidiče (pracovní role `WorkPersonFacility`). */
const driverRefSchema = z.object({
  workPersonFacilityId: z.number(),
  name: z.string(),
});

/** Volitelná celočíselná hodnota zapsaná do textového pole — prázdný řetězec = nevyplněno. */
function optionalInteger(message: string, min: number, max: number = Number.MAX_SAFE_INTEGER) {
  return z.string().refine((raw) => {
    if (raw.trim() === '') {
      return true;
    }
    const value = Number(raw);
    return Number.isInteger(value) && value >= min && value <= max;
  }, message);
}

/** Povinná celočíselná hodnota v textovém poli. */
function requiredInteger(message: string, min: number) {
  return z.string().refine((raw) => {
    const value = Number(raw);
    return raw.trim() !== '' && Number.isInteger(value) && value >= min;
  }, message);
}

/**
 * Factory Zod schématu detailu vozu — UI zrcadlo BE validace; hlášky z `t` až za běhu.
 */
export function buildVehicleFormSchema(t: TFunction) {
  return z
    .object({
      name: z.string().trim().min(1, t('vehicle.validation.nameRequired')),
      plate: z
        .string()
        .trim()
        .min(1, t('vehicle.validation.plateRequired'))
        .max(50, t('vehicle.validation.plateMaxLength')),
      owner: organizationRefSchema.nullable(),
      category: codebookRefSchema.nullable(),
      group: codebookRefSchema.nullable(),
      manufacturer: codebookRefSchema.nullable(),
      yearOfManufacture: optionalInteger(t('vehicle.validation.yearInvalid'), 1900, 2200),
      initialMileageKm: optionalInteger(t('vehicle.validation.mileageNonNegative'), 0, 100_000_000),
      fuelType: z.enum(FuelType).nullable(),
      vin: z.string().max(50, t('vehicle.validation.vinMaxLength')),
      passengerSeats: requiredInteger(t('vehicle.validation.passengerSeatsPositive'), 1),
      tourLeaderSeats: optionalInteger(t('vehicle.validation.tourLeaderSeatsNonNegative'), 0),
      displayOnResSheetWithRidesOnly: z.boolean(),
      commissioningDate: z.string().nullable(),
      retirementDate: z.string().nullable(),
      insuranceExpiry: z.string().nullable(),
      inspectionExpiry: z.string().nullable(),
      tachographInspectionExpiry: z.string().nullable(),
      preferredDrivers: z.array(driverRefSchema),
    })
    .superRefine((values, ctx) => {
      // Povinná pole podle Magicu: prázdné datum přijde z `DateBox` jako `''`, ne jako `null`.
      const required = [
        ['owner', values.owner, 'vehicle.validation.ownerRequired'],
        ['category', values.category, 'vehicle.validation.categoryRequired'],
        ['group', values.group, 'vehicle.validation.groupRequired'],
        ['manufacturer', values.manufacturer, 'vehicle.validation.manufacturerRequired'],
        [
          'commissioningDate',
          values.commissioningDate,
          'vehicle.validation.commissioningDateRequired',
        ],
      ] as const;
      for (const [path, value, message] of required) {
        if (value == null || value === '') {
          ctx.addIssue({ code: 'custom', path: [path], message: t(message) });
        }
      }

      if (
        values.commissioningDate &&
        values.retirementDate &&
        values.retirementDate < values.commissioningDate
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['retirementDate'],
          message: t('vehicle.validation.retirementBeforeCommissioning'),
        });
      }

      const driverIds = values.preferredDrivers.map((driver) => driver.workPersonFacilityId);
      if (new Set(driverIds).size !== driverIds.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['preferredDrivers'],
          message: t('vehicle.validation.duplicateDrivers'),
        });
      }
    });
}

export type VehicleFormValues = z.infer<ReturnType<typeof buildVehicleFormSchema>>;

/** ISO datum (`YYYY-MM-DD`) z API hodnoty; prázdná hodnota → `null`. */
function toIsoDate(value: string | null | undefined): string | null {
  return value ? value.slice(0, 10) : null;
}

export function emptyVehicleFormValues(): VehicleFormValues {
  return {
    name: '',
    plate: '',
    owner: null,
    category: null,
    group: null,
    manufacturer: null,
    yearOfManufacture: '',
    initialMileageKm: '',
    fuelType: null,
    vin: '',
    passengerSeats: '',
    tourLeaderSeats: '0',
    displayOnResSheetWithRidesOnly: false,
    // Datum prvního uvedení do provozu je povinné a předvyplňuje se dneškem (lokální den, ne UTC).
    commissioningDate: dateToIso(new Date()),
    retirementDate: null,
    insuranceExpiry: null,
    inspectionExpiry: null,
    tachographInspectionExpiry: null,
    preferredDrivers: [],
  };
}

/**
 * Mapuje detail vozu z API na hodnoty formuláře (režim editace). Nevalidní historický záznam se
 * načte bez omezení — validace se uplatní až při uložení, aby ho šlo v UI opravit.
 */
export function fromGetVehicleResponse(response: GetVehicleResponse): VehicleFormValues {
  return {
    name: response.name ?? '',
    plate: response.plate ?? '',
    owner: {
      partyId: response.ownerOrganizationPartyId,
      name: response.ownerName ?? '',
    },
    category: { id: response.vehicleCategoryCbId, name: response.categoryName ?? '', code: null },
    group: { id: response.vehicleGroupCbId, name: response.groupName ?? '', code: null },
    manufacturer: {
      id: response.vehicleManufacturerCbId,
      name: response.manufacturerName ?? '',
      code: null,
    },
    yearOfManufacture: response.yearOfManufacture?.toString() ?? '',
    initialMileageKm: response.initialMileageKm?.toString() ?? '',
    fuelType: response.fuelType ?? null,
    vin: response.vin ?? '',
    passengerSeats: response.passengerSeats.toString(),
    tourLeaderSeats: response.tourLeaderSeats.toString(),
    displayOnResSheetWithRidesOnly: response.displayOnResSheetWithRidesOnly,
    commissioningDate: toIsoDate(response.commissioningDate),
    retirementDate: toIsoDate(response.retirementDate),
    insuranceExpiry: toIsoDate(response.insuranceExpiry),
    inspectionExpiry: toIsoDate(response.inspectionExpiry),
    tachographInspectionExpiry: toIsoDate(response.tachographInspectionExpiry),
    preferredDrivers: response.preferredDrivers.map((driver) => ({
      workPersonFacilityId: driver.workPersonFacilityId,
      name: driver.name ?? '',
    })),
  };
}
